import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ScreenshotGallery.css';

interface Screenshot {
  src: string;
  name: string;
}

const ScreenshotGallery: React.FC = () => {
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Generate screenshot list dynamically
    const loadScreenshots = () => {
      const screenshotList: Screenshot[] = [];
      
      // Try to load screenshots 01-20 (you can extend this range)
      for (let i = 1; i <= 10; i++) {
        const paddedNumber = i.toString().padStart(2, '0');
        const src = `/screenshots/screenshot-${paddedNumber}.png`;
        const name = `screenshot-${paddedNumber}.png`;
        
        // Check if image exists by trying to load it
        const img = new Image();
        img.onload = () => {
          screenshotList.push({ src, name });
          setScreenshots([...screenshotList].sort((a, b) => a.name.localeCompare(b.name)));
        };
        img.src = src;
      }
    };

    loadScreenshots();
  }, []);

  const calculateScale = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;
    
    // Wait for image to load
    if (img.naturalWidth === 0 || img.naturalHeight === 0) return;

    const containerRect = container.getBoundingClientRect();
    const maxWidth = containerRect.width - 40; // padding
    const maxHeight = containerRect.height - 40; // padding

    const scaleX = maxWidth / img.naturalWidth;
    const scaleY = maxHeight / img.naturalHeight;
    
    // Use the smaller scale to ensure image fits both dimensions
    const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
    
    setImageScale(newScale);
  }, []);

  const openModal = (index: number) => {
    setSelectedIndex(index);
    setIsModalOpen(true);
    setImageScale(1);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIndex(null);
    setImageScale(1);
  }, []);

  const goToNext = useCallback(() => {
    setSelectedIndex(prev => {
      if (prev !== null && prev < screenshots.length - 1) {
        setImageScale(1);
        return prev + 1;
      }
      return prev;
    });
  }, [screenshots.length]);

  const goToPrevious = useCallback(() => {
    setSelectedIndex(prev => {
      if (prev !== null && prev > 0) {
        setImageScale(1);
        return prev - 1;
      }
      return prev;
    });
  }, []);

  const handleImageLoad = useCallback(() => {
    // Calculate scale after image loads
    setTimeout(calculateScale, 50);
  }, [calculateScale]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isModalOpen) return;
    
    switch (e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case 'ArrowLeft':
        goToPrevious();
        break;
    }
  }, [isModalOpen, closeModal, goToNext, goToPrevious]);

  // Recalculate scale on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isModalOpen) {
        setTimeout(calculateScale, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isModalOpen, calculateScale]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="screenshot-gallery">
      <div className="gallery-grid">
        {screenshots.map((screenshot, index) => (
          <div 
            key={screenshot.name}
            className="gallery-item" 
            onClick={() => openModal(index)}
          >
            <img 
              src={screenshot.src} 
              alt={`Screenshot ${index + 1}`}
              loading="lazy"
            />
            <div className="gallery-overlay">
              <div className="view-icon">👁️</div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && selectedIndex !== null && (
        <div className="screenshot-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✕</button>
            
            <div className="modal-navigation">
              <button 
                className="nav-btn prev" 
                onClick={goToPrevious}
                disabled={selectedIndex === 0}
              >
                ❮
              </button>
              
              <div className="modal-image-container" ref={containerRef}>
                <img 
                  ref={imageRef}
                  src={screenshots[selectedIndex].src} 
                  alt={`Screenshot ${selectedIndex + 1}`}
                  onLoad={handleImageLoad}
                  style={{
                    transform: `scale(${imageScale})`,
                    transition: 'transform 0.3s ease'
                  }}
                />
              </div>
              
              <button 
                className="nav-btn next" 
                onClick={goToNext}
                disabled={selectedIndex === screenshots.length - 1}
              >
                ❯
              </button>
            </div>
            
            <div className="modal-info">
              <span className="image-counter">
                {selectedIndex + 1} / {screenshots.length}
              </span>
              {imageScale < 1 && (
                <span className="scale-info">
                  Scaled to {Math.round(imageScale * 100)}% to fit
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenshotGallery; 